import { AiDraftItem } from "@/pages/Private/pages/Messages/components/GenerateBar/types";

import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { SenderRecipientOption } from "../components/SenderRecipientDropdown";

import {
	Draft,
	DraftRecipient,
	DraftSender,
	createDefaultDraft,
	timingFromDays,
} from "./inboxDraft.types";
import { buildSenderOptions } from "./buildSenderOptions.util";

import { aiItemIsInvitation } from "./pendingInvitation.util";
import { resolveInboxChannel } from "./resolveInboxChannel.util";
import {
	pickAiSenderOption,
	preferThreadSender,
	type AiSender,
} from "./resolveAiSenderUserId.util";
import { findMeAmongSenders, pickMyOption } from "./resolveMyDefaultSender.util";

import type { LinkedinSenderStates } from "./filterLinkedinSenders.util";

import type { User } from "@/pages/Public/pages/Login/schema/login";

interface BuildFilteredAiDraftsParams {
	items: AiDraftItem[];
	listPersonId: number | undefined;
	prospectEmail: string | null | undefined;
	prospectHasLinkedinUrl: boolean;
	conversations: Conversation[];
	senderOptions: SenderRecipientOption[];
	recipientOptions: SenderRecipientOption[];
	user: User | null | undefined;
	sendingUsers: { data?: User[] } | undefined;
	connectionCheckDone: boolean;
	connectedUserIds: Set<number>;
	/** Pending and unknown senders, for the rebuilt options' labels (PREC-4216). */
	senderStates?: LinkedinSenderStates;
	startingDraftId: number;
	aiSender?: AiSender;
}

export interface BuildFilteredAiDraftsResult {
	drafts: Draft[];
	filteredItems: AiDraftItem[];
	activeChannel: "email" | "linkedin" | undefined;
	hasInvite: boolean;
	nextDraftId: number;
}

/**
 * Convert AiDraftItem[] into Draft[] with:
 * - LinkedIn invite handling (eager inclusion of unconnected LinkedIn senders
 *   when any item is an invite note).
 * - Channel resolution via resolveInboxChannel and filtering items to the
 *   active channel.
 * - Per-item sender/recipient selection (LinkedIn drafts pick a LinkedIn sender
 *   from the effective options; Email drafts use the resolved sender).
 *
 * Pure: does not mutate refs or dispatch anything. Callers are responsible for
 * setting state and updating their own id counters from `nextDraftId`.
 */
export const buildFilteredAiDrafts = ({
	items,
	listPersonId,
	prospectEmail,
	prospectHasLinkedinUrl,
	conversations,
	senderOptions,
	recipientOptions,
	user,
	sendingUsers,
	connectionCheckDone,
	connectedUserIds,
	senderStates,
	startingDraftId,
	aiSender,
}: BuildFilteredAiDraftsParams): BuildFilteredAiDraftsResult => {
	const hasInvite = items.some((item) => item.isInviteNote);

	// Sender options always include unconnected LinkedIn senders now (the
	// invite-note flag is applied automatically when the chosen LinkedIn sender
	// is not connected). We still rebuild the list when an invite is present
	// to make sure LinkedIn senders are picked up even if the caller's
	// `senderOptions` snapshot pre-dates the connection check.
	const effectiveSenderOptions =
		hasInvite && sendingUsers?.data
			? buildSenderOptions({
					users: sendingUsers.data,
					connectionCheckDone,
					connectedUserIds,
					senderStates,
					prospectHasEmail: !!prospectEmail,
					prospectHasLinkedinUrl,
					currentUserId: user?.id,
				})
			: senderOptions;

	const {
		channelType,
		senderOption,
		recipientOption: resolvedRecipientOption,
	} = resolveInboxChannel(conversations, listPersonId, effectiveSenderOptions, recipientOptions);

	// The single channel whose items are rendered: the conversation history's channel
	// (resolved even when no genuine sender matched — PREC-3627 deliberately leaves
	// `senderOption` empty rather than guessing), else the sequence's entry step for a
	// lead with no history. Without these fallbacks nothing was filtered and a
	// mixed-channel sequence rendered ALL its steps (email follow-ups under a
	// LinkedIn invite).
	const activeChannel = channelType ?? items[0]?.channel;
	const filteredItems = activeChannel
		? items.filter((item) => item.channel === activeChannel)
		: items;

	// When the channel came from the fallback (no history), still address the drafts
	// to the matching recipient instead of an empty "Recipient" placeholder.
	const recipientOption =
		resolvedRecipientOption ?? recipientOptions.find((opt) => opt.type === activeChannel);

	let nextDraftId = startingDraftId;

	const drafts: Draft[] = filteredItems.map((item, idx) => {
		const aiPicked = pickAiSenderOption(effectiveSenderOptions, item.channel, aiSender);

		const itemSender =
			preferThreadSender(aiPicked, senderOption) ??
			senderOption ??
			pickMyOption(
				effectiveSenderOptions,
				item.channel,
				findMeAmongSenders(sendingUsers?.data, user?.id) ?? user ?? undefined
			);

		const fromSender: DraftSender = itemSender
			? {
					userId: itemSender.userId ?? user?.id ?? 0,
					type: item.channel,
					label: itemSender.label,
					emailId: itemSender.emailId,
					emailAddress: itemSender.emailAddress,
				}
			: {
					userId: user?.id ?? 0,
					type: item.channel,
					label: "Sender",
				};

		const toRecipient: DraftRecipient = recipientOption
			? {
					type: item.channel,
					label: recipientOption.label,
					value: recipientOption.value ?? "",
				}
			: {
					type: item.channel,
					label: "Recipient",
					value: "",
				};

		const id = nextDraftId++;

		const draft = createDefaultDraft(
			id,
			fromSender,
			toRecipient,
			item.content,
			timingFromDays(item.scheduledInDays ?? (idx === 0 ? 0 : idx * 3))
		);

		if (item.subject) {
			draft.subject = item.subject;
		}

		// A sender whose own invitation is pending cannot send a second one (PREC-4216).
		if (aiItemIsInvitation(item, fromSender.userId, senderStates?.pendingInvitations)) {
			draft.isInviteNote = true;
		}

		if (item.aiOrigin) {
			draft.aiOrigin = item.aiOrigin;
		}

		return draft;
	});

	return {
		drafts,
		filteredItems,
		activeChannel,
		hasInvite,
		nextDraftId,
	};
};
